import GroupWorkIcon from "@mui/icons-material/GroupWork";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { Tooltip } from "@mui/material";
import LargeSwitch from "components/shared/LargeSwitch";
import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import type { ChannelParams, GroupParams, ITermFormData } from "containers/TermFormContainer";
import { showsDefaultChannelConfiguration } from "containers/TermFormContainer/utils";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";

import variables from "../../../../assets/styles/_variables.module.scss";
import ChannelBaseColumnsFields, { channelUsesSalesBaseColumns } from "../ChannelBaseColumnsFields";
import GroupMilestonesInput from "../GroupMilestonesInput";
import { resolveChannelMilestoneBaseColumn } from "../milestoneBaseColumnUtils";
import type { GroupWithChannels } from "../types";
import ShowDefaultConfigurationField from "../ShowDefaultConfigurationField";
import GroupFormatOverrides from "./GroupFormatOverrides";
import GroupPercentages from "./GroupPercentages";

interface IGroupSection {
    group: GroupWithChannels;
    /**
     * Is group active on current form?
     */
    groupCurrentlyActive: boolean;
    /**
     * Is group active on the title (other contract)?
     */
    groupActiveOnTitle: boolean;
    /**
     * (useState) array that contains channels chosen within the form and their data.
     */
    chosenChannels: ChannelParams[];
    /**
     * whether it is the creation form or edit one.
     */
    mode: "new" | "edit";
    /**
     * Handles saving changes to percentages/tiers of channels when group is unsynced.
     */
    handleChannelUpdates: (newChannel: any, groupId?: number) => void;
    handleToggleFormatTypeOverride: (channelId: number, formatTypeId: number, enabled: boolean) => void;
    handleGroupCalculationBaseColumnChange: (
        groupId: number,
        calculationBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
        applyToEveryFormula?: boolean,
    ) => void;
    handleGroupMilestoneColumnChange: (
        groupId: number,
        milestoneBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
        applyToEveryFormula?: boolean,
    ) => void;
    handleToggleGroupFormatTypeOverride: (group: GroupWithChannels, formatTypeId: number, enabled: boolean) => void;
    calculationBaseColumnLabels: ICalculationBaseColumnLabel[];
    formatTypesOnWork?: { id: number; name: string }[];
    /**
     * Handles activating group on current form.
     */
    handleGroupActivation: (e: React.ChangeEvent<HTMLInputElement>, group: GroupWithChannels) => void;
    /**
     * Handles updates of data within group (whether synced or not)
     */
    handleGroupUpdate: (newGroupData: any, groupChannels: IChannel[], formatTypeId?: number | null) => void;
    /**
     * Copies the first channel's default + override formulas onto the other group channels.
     */
    handleAlignGroupOnSync: (group: GroupWithChannels) => void;
    /**
     * When group is synced, needed to know if channels accept tiers or not.
     */
    hasMilestones: boolean;
    /**
     * Data of group when coming from edit mode.
     */
    groupOnEdit: GroupParams | undefined;
    /**
     * (useState) to save the chosenGroups.
     */
    setChosenGroups: Dispatch<SetStateAction<GroupParams[]>>;
    /**
     * (useState) array that contains groups chosen within the form and their data.
     */
    chosenGroups: GroupParams[];
    /**
     * Triggers save button on edit form.
     */
    onChange: VoidFunction | undefined;
    /**
     * Whether they have used template to complete form.
     */
    usesTemplate: boolean | undefined;
    useFormData: Pick<ITermFormData, "setError" | "clearErrors">;
    from?: "templates" | "form";
    onShowDefaultConfigurationChange?: (channelId: number, showDefaultConfiguration: boolean) => void;
    onGroupShowDefaultConfigurationChange?: (group: GroupWithChannels, showDefaultConfiguration: boolean) => void;
}

/**
 * Handles displaying groups: whether synced or unsynced.
 * Calculation / milestone bases are always group-scoped; only milestone ladders can diverge when unsynced.
 */
const GroupSection = ({
    group,
    groupCurrentlyActive,
    groupActiveOnTitle,
    chosenChannels,
    mode,
    handleChannelUpdates,
    handleToggleFormatTypeOverride,
    handleGroupCalculationBaseColumnChange,
    handleGroupMilestoneColumnChange,
    handleToggleGroupFormatTypeOverride,
    calculationBaseColumnLabels,
    formatTypesOnWork = [],
    handleGroupActivation,
    handleGroupUpdate,
    handleAlignGroupOnSync,
    hasMilestones,
    groupOnEdit,
    setChosenGroups,
    chosenGroups,
    onChange,
    usesTemplate,
    useFormData,
    from = "form",
    onShowDefaultConfigurationChange,
    onGroupShowDefaultConfigurationChange,
}: IGroupSection) => {
    const [t] = useTranslation();
    const { setError, clearErrors } = useFormData;

    // Used to get info of whether or not group is synced (both on new and edit mode)
    const groupData = useMemo(() => {
        return mode === "new" ? chosenGroups.find((item) => item.id === group.id) ?? group : groupOnEdit;
    }, [mode, chosenGroups, group, groupOnEdit]);

    const [channelsSynced, setChannelsSynced] = useState(groupData?.synced);

    const referenceChannel = useMemo(
        () => group.channels.find(channelUsesSalesBaseColumns) ?? group.channels[0],
        [group.channels],
    );
    const referenceExistingChannel = useMemo(
        () => chosenChannels.find((item) => item.channelId === referenceChannel?.id && item.formatTypeId == null),
        [chosenChannels, referenceChannel?.id],
    );

    const handleGroupSync = (isSynced: boolean) => {
        setChannelsSynced(isSynced);
        if (isSynced) {
            handleAlignGroupOnSync(group);
            return;
        }
        setChosenGroups((prev) => prev.map((item) => (item.id === group.id ? { ...item, synced: false } : item)));
        onChange && onChange();
    };

    useEffect(() => {
        setChannelsSynced(groupData?.synced);
    }, [mode, groupOnEdit, group, groupData?.synced]);

    const showGroupBaseColumns =
        groupCurrentlyActive && !!referenceChannel && channelUsesSalesBaseColumns(referenceChannel);
    const referenceIsProportional = referenceChannel?.calculationMethod === CalculationMethod.PROPORTIONAL;
    const showSyncedDefault = showsDefaultChannelConfiguration(referenceExistingChannel);
    const hideSyncedDefault = !!channelsSynced && referenceIsProportional && !showSyncedDefault;

    return (
        <div className="channel-line" key={`group-${group.id}`}>
            <div className="channel-top">
                <div className="channel-title group">
                    <GroupWorkIcon
                        sx={{
                            color: `${groupCurrentlyActive ? "#141516" : variables["foreground"]}`,
                        }}
                    />
                    <div className="group-names">
                        <span
                            style={{
                                color: `${groupCurrentlyActive ? "#141516" : variables["foreground"]}`,
                            }}
                        >
                            {group.name}
                        </span>
                        <span
                            className="channel-names"
                            style={{
                                color: `${groupCurrentlyActive ? "#141516" : variables["foreground"]}`,
                            }}
                        >
                            {group.channels.map((channel) => channel.name).join(", ")}
                        </span>
                    </div>
                </div>
                <div className="switch">
                    {groupActiveOnTitle && !groupCurrentlyActive && (
                        <div className="channel-reminder">
                            <div className="chip" />
                            {t("ConnectTitle.ActiveChannel")}
                        </div>
                    )}
                    {groupCurrentlyActive && (
                        <Tooltip title={channelsSynced ? "Désynchroniser les canaux" : "Synchroniser les canaux"}>
                            {channelsSynced ? (
                                <LinkOffIcon
                                    sx={{ cursor: "pointer", width: "20px" }}
                                    onClick={() => handleGroupSync(!channelsSynced)}
                                />
                            ) : (
                                <LinkIcon
                                    sx={{ cursor: "pointer", width: "20px" }}
                                    onClick={() => handleGroupSync(!channelsSynced)}
                                />
                            )}
                        </Tooltip>
                    )}
                    <LargeSwitch
                        onChange={(e: any) => handleGroupActivation(e, group)}
                        inputProps={{ "aria-label": "controlled" }}
                        checked={groupCurrentlyActive}
                        className={groupCurrentlyActive && usesTemplate ? "template" : ""}
                    />
                </div>
            </div>
            {showGroupBaseColumns && (
                <div className="channel-config">
                    {from === "templates" && channelsSynced && referenceIsProportional && (
                        <ShowDefaultConfigurationField
                            checked={showSyncedDefault}
                            onChange={(showDefault) => onGroupShowDefaultConfigurationChange?.(group, showDefault)}
                        />
                    )}
                    {!hideSyncedDefault && (
                        <ChannelBaseColumnsFields
                            channel={{ ...referenceChannel, hasMilestones: hasMilestones ? 1 : 0 }}
                            existingChannel={referenceExistingChannel}
                            calculationBaseColumnLabels={calculationBaseColumnLabels}
                            onCalculationBaseColumnChange={(_channelId, calculationBaseColumn) =>
                                handleGroupCalculationBaseColumnChange(
                                    group.id,
                                    calculationBaseColumn,
                                    null,
                                    !channelsSynced,
                                )
                            }
                            onMilestoneColumnChange={(_channelId, milestoneBaseColumn) =>
                                handleGroupMilestoneColumnChange(group.id, milestoneBaseColumn, null, !channelsSynced)
                            }
                        />
                    )}
                    {channelsSynced && !hideSyncedDefault && (
                        <div className="channel-milestones">
                            <GroupMilestonesInput
                                hasMilestones={!!hasMilestones}
                                group={group}
                                milestoneBaseColumn={resolveChannelMilestoneBaseColumn(
                                    referenceChannel,
                                    referenceExistingChannel,
                                )}
                                onChange={(newGroupData: any) => handleGroupUpdate(newGroupData, group.channels)}
                                propMilestones={groupOnEdit?.data}
                                useFormData={{ setError, clearErrors }}
                            />
                        </div>
                    )}
                    {channelsSynced && (
                        <GroupFormatOverrides
                            group={group}
                            formatTypesOnWork={formatTypesOnWork}
                            chosenChannels={chosenChannels}
                            calculationBaseColumnLabels={calculationBaseColumnLabels}
                            hasMilestones={hasMilestones}
                            useFormData={{ setError, clearErrors }}
                            handleToggleGroupFormatTypeOverride={(formatTypeId, enabled) =>
                                handleToggleGroupFormatTypeOverride(group, formatTypeId, enabled)
                            }
                            handleGroupCalculationBaseColumnChange={handleGroupCalculationBaseColumnChange}
                            handleGroupMilestoneColumnChange={handleGroupMilestoneColumnChange}
                            handleGroupUpdate={handleGroupUpdate}
                        />
                    )}
                </div>
            )}
            {!channelsSynced &&
                groupCurrentlyActive &&
                group.channels.map((channel, index) => {
                    const existingChannel = chosenChannels.find(
                        (item) => item.channelId === channel.id && item.formatTypeId == null,
                    );

                    return (
                        <div className="group-channels" key={`group-percentage-${channel.id}`}>
                            <GroupPercentages
                                index={index + 1}
                                channel={channel}
                                currentlyActive={groupCurrentlyActive}
                                existingChannel={existingChannel}
                                chosenChannels={chosenChannels}
                                formatTypesOnWork={formatTypesOnWork}
                                calculationBaseColumnLabels={calculationBaseColumnLabels}
                                mode={mode}
                                handleChannelUpdates={handleChannelUpdates}
                                handleToggleFormatTypeOverride={handleToggleFormatTypeOverride}
                                group={group}
                                useFormData={{ setError, clearErrors }}
                                from={from}
                                onShowDefaultConfigurationChange={onShowDefaultConfigurationChange}
                            />
                        </div>
                    );
                })}
        </div>
    );
};

export default GroupSection;
